import { useCallback, useEffect, useMemo, useRef, useState, FC, ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import SVGImage from '~/components/Image/SVGImage'
import { fromEntries } from '~/utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '../../hooks/useFocus'

import { LatticeCarriedNumber, LatticeSumInput } from './components/LatticeSumInput'
import { LatticeCell } from './components/LatticeCell'
import { LatticeDigit } from './components/LatticeDigit'
import { generateLattice, generateLatticeGridTemplate, getCellDefaultValues, getSumCellDefaultValues } from './helpers'
import { Cell, CellName, LatticeRow, LatticeRowType } from './types'

const LatticeInteger: FC<ActivityProps> = ({
  difficultyLevel,
  actionButtonsFC,
  answerState,
  onAnswerStateChange,
  onNextQuestionClick,
  onInstanceChange,
  autofocus = true,
}) => {
  const [latticeArray, setLatticeArray] = useState<LatticeRow[]>([])
  const [gridColumns, setGridColumns] = useState<number>(0)
  const [gridRows, setGridRows] = useState<number>(0)
  const [multiplier, setMultiplier] = useState<string>('')
  const [multiplicand, setMultiplicand] = useState<string>('')
  const [hasCarriedMultiplicand, setHasCarriedMultiplicand] = useState<boolean>(true)
  const [answerCells, setAnswerCells] = useState<Record<string, Cell>>()
  const [errors, setErrors] = useState<string[]>([])
  const [cellsSumAnswers, setCellsSumAnswers] = useState<Record<string, string>>({})
  const [carriedNumbers, setCarriedNumber] = useState<Record<string, string>>({})
  const [inputKey, setInputKey] = useState<string>(uuid())
  const { t } = useTranslation('imageAlt')

  const { givenAnswer, isFormValid } = useMemo(() => {
    const values = Object.values(cellsSumAnswers)
    const _isFormValid = values.some((item) => Boolean(item))

    return { givenAnswer: values.join(''), isFormValid: _isFormValid }
  }, [cellsSumAnswers])

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const generateActivity = useCallback(() => {
    const latticeData = generateLattice(difficultyLevel)
    const _hasCarriedMultiplicand = latticeData.latticeArray.some((item) => {
      return item.type === LatticeRowType.NUM && item.hasCarriedNumber
    })

    setLatticeArray(latticeData.latticeArray)
    setGridColumns(latticeData.gridColumns)
    setGridRows(latticeData.gridRows)
    setMultiplier(latticeData.multiplier)
    setMultiplicand(latticeData.multiplicand)
    setHasCarriedMultiplicand(_hasCarriedMultiplicand)
    setAnswerCells(getCellDefaultValues(latticeData.gridColumns + latticeData.gridRows))
    setCellsSumAnswers(getSumCellDefaultValues(latticeData.latticeArray))
  }, [difficultyLevel])

  useEffect(() => {
    generateActivity()
  }, [generateActivity])

  // Only the two factors — the worked lattice is the answer.
  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplier} \\cdot ${multiplicand} = \\square` : null,
  )

  const handleCellValueChange = (value: string, name: CellName): void => {
    const [sumIndex] = name.split(':') as [string, CellName]
    setAnswerCells((prev) => {
      const modified = { ...prev }
      modified[sumIndex] = {
        ...modified[sumIndex],
        [name]: value,
      }

      return modified
    })
  }

  const handleSumValueChange = (value: string, name: string): void => {
    setCellsSumAnswers((prev) => ({ ...prev, [name]: value }))
  }

  const handleCarriedNumberChange = (value: string, name: string): void => {
    setCarriedNumber((prev) => ({ ...prev, [name]: value }))
  }

  const handleOnNextQuestionClick = (): void => {
    clear()
    onNextQuestionClick()
    generateActivity()
    onAnswerStateChange(null)
    setInputKey(uuid())
  }

  function handleOnCheckAnswerClick(): void {
    const sumCellsArr = Object.entries(cellsSumAnswers)
    let ansArr = new Array(gridColumns + gridRows).fill(undefined)

    const answer = String(Number(multiplicand) * Number(multiplier))
    ansArr = ansArr.slice(0, sumCellsArr.length - answer.length).concat(...Array.from(answer))

    const isCorrectAns = Number(answer) === Number(givenAnswer)

    let sumErrors: string[] = []

    sumErrors = sumCellsArr
      .filter(([, value], index) => {
        const ansDigit = ansArr[index]
        if (!Number(value) && !ansDigit) {
          return false
        }
        return ansDigit !== value
      })
      .map(([key]) => key)

    if (!sumErrors.length && isCorrectAns) {
      onAnswerStateChange(true)
    } else {
      onAnswerStateChange(false)
    }

    if (sumErrors.length === 0) {
      setErrors([])
      return
    }

    setErrors(sumErrors)
  }

  const onEnterPress = (): void => {
    hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
  }

  const clear = (): void => {
    setAnswerCells(getCellDefaultValues(gridRows + gridColumns))
    setCellsSumAnswers((prev) => {
      const cleared: Array<[string, string]> = Object.entries(prev).map(([key]) => [key, ''])
      return fromEntries(cleared)
    })
    setCarriedNumber({})
    setErrors([])
  }

  function renderItem(item: LatticeRow, index: number): ReactNode {
    switch (item.type) {
      case LatticeRowType.CELL: {
        const [field1Name, field2Name] = item.names as CellName[]
        const value1 = answerCells?.[field1Name.split(':')[0]]?.[field1Name] || ''
        const value2 = answerCells?.[field2Name.split(':')[0]]?.[field2Name] || ''

        return (
          <LatticeCell
            key={`${item.name}-${index}-${inputKey}-cell`}
            names={item.names}
            onChange={handleCellValueChange}
            onEnter={onEnterPress}
            values={[value1, value2]}
          />
        )
      }
      case LatticeRowType.NUM:
        return (
          <LatticeDigit
            carriedValue={item.carriedName && carriedNumbers[item.carriedName] ? carriedNumbers[item.carriedName] : ''}
            digit={item.value}
            hasCarriedMultiplicand={hasCarriedMultiplicand}
            hasCarriedNumber={!!item.hasCarriedNumber}
            key={`${item.itemIndex}-${item.carriedName}-${inputKey}-digit`}
            name={item.carriedName || ''}
            onCarriedChange={handleCarriedNumberChange}
            onEnter={onEnterPress}
          />
        )

      case LatticeRowType.SUM:
        return (
          <LatticeSumInput
            inputRef={item.itemIndex === gridColumns + gridRows ? inputRef : null}
            isError={errors.includes(item.name)}
            key={`${item.name}-${index}-${inputKey}-sum`}
            name={item.name}
            onChange={handleSumValueChange}
            onEnter={onEnterPress}
            value={cellsSumAnswers[item.name]}
          />
        )

      case LatticeRowType.CARR:
        return (
          <LatticeCarriedNumber
            key={`${item.name}-${index}-${inputKey}-carried`}
            name={item.name}
            onChange={handleCarriedNumberChange}
            onEnter={onEnterPress}
            value={carriedNumbers[item.name] || ''}
          />
        )

      default:
        return <div />
    }
  }

  const gridTemplateAreas = generateLatticeGridTemplate(gridColumns, gridRows)

  return (
    <ActivityWrapper className="mt-7">
      <div
        className="grid space-x-2 space-y-2"
        style={{
          gridTemplateAreas,
          justifyItems: 'center',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ gridArea: 'space' }} />
        <div style={{ gridArea: 'space2' }} />
        <div style={{ gridArea: 'carriedSpace' }} />
        <div className="font-semibold text-3xl text-purple w-4" style={{ gridArea: 'times', width: 14, height: 14 }}>
          <SVGImage alt={t('timesIcon')} srcKey="bootcamp/multi_sign.svg" />
        </div>
        {latticeArray.map((item, index) => renderItem(item, index))}
      </div>

      {actionButtonsFC &&
        actionButtonsFC({
          formValid: isFormValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: clear,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default LatticeInteger
